import {
	defaultKSK2140_R01DetailDto,
	defaultKSK2140_R01LotDto,
	KSK2140_R01DetailDto,
	KSK2140_R01HeaderDto,
	KSK2140_R01InspectDto,
	KSK2140_R01InspectMap,
	KSK2140_R01LotDto,
} from "./Dto_KSK2140_R01";

export const headerNames_KSK2140_R01: Record<string, string> = {
	rowIndex: "",
	productResultNo: "생산실적번호",
	compCd: "사업장",
	productDt: "생산일자",
	workplaceCd: "공정명",
	lotNo: "LOT 번호",
	itemCd: "품목코드",
	itemNm: "품명",
	itemSpec: "규격",
	itemUnit: "단위",
	orderQty: "지시량",
	productEmp: "작업자",
	productQty: "생산량",
	processLotQty: "미검사량",
	lotQty: "재고량",
};

export const headerNames_KSK2140_R01Detail: Record<string, string> = {
	rowIndex: "",
	itemCd: "품목코드",
	itemNm: "품명",
	inspectQty: "검사량",
	lotQty: "미검사량",
	processLotQty: "실적량",
	basLotNo: "LOT 번호",
	lotCreateDt: "최초 입고일",
	acceptQty: "합격량",
	packQty: "포장량",
	checkFinish: "검사여부",
};

/** 목록 행 고유키 — 동일 생산실적에 LOT만 다른 행 구분 */
export const getKSK2140_R01HeaderKey = (row: {
	productResultNo?: string;
	lotNo?: string;
	valueIndex?: number;
}) => {
	if (row.valueIndex != null && row.valueIndex > 0) return `vi:${row.valueIndex}`;
	return `${row.productResultNo || ""}|${row.lotNo || ""}`;
};

/** Inspect map key — 상세 행의 basOrderNo + "_" + lotNo */
export const getKSK2140_R01InspectKey = (row: {
	basOrderNo?: string;
	lotNo?: string;
	basLotNo?: string;
}) => `${row.basOrderNo || ""}_${row.lotNo || row.basLotNo || ""}`;

/** Inspect Map을 저장용 Record로 만들고, 키의 실적/LOT을 각 행에 채움 */
export const flattenKSK2140_R01InspectMap = (
	inspectMap: Map<string, KSK2140_R01InspectDto[]>,
	detailList: KSK2140_R01DetailDto[],
): KSK2140_R01InspectMap => {
	const saveMap: KSK2140_R01InspectMap = {};

	detailList.forEach((detail) => {
		const key = getKSK2140_R01InspectKey(detail);
		const inspectList = (inspectMap.get(key) ?? []).map((item) => ({
			...item,
			basOrderNo: detail.basOrderNo || "",
			lotNo: detail.lotNo || detail.basLotNo || "",
			basLotNo: detail.basLotNo || detail.lotNo || "",
			itemCd: item.itemCd || detail.itemCd || "",
		}));

		if (inspectList.length > 0) {
			saveMap[key] = inspectList;
		}
	});

	return saveMap;
};

/** Header → Detail (PrdInspectResultDto 필드 매핑) */
export const toKSK2140_R01DetailDto = (header: KSK2140_R01HeaderDto): KSK2140_R01DetailDto => {
	const processLotQty = Number(header.processLotQty || 0);
	return {
		...defaultKSK2140_R01DetailDto,
		compCd: header.compCd || "",
		inspectDt: header.productDt,
		workplaceCd: header.workplaceCd || "",
		workplaceNm: header.workplaceNm || "",
		jobTeam: header.jobTeam || "",
		itemCd: header.itemCd || "",
		itemNm: header.itemNm || "",
		workerEmp: header.productEmp || "",
		workerEmpNm: header.productEmpNm || "",
		inspectQty: 0,
		acceptQty: 0,
		packQty: Number(header.packQty || 0),
		lotQty: processLotQty,
		processLotQty,
		basOrderNo: header.productResultNo || "",
		basLotNo: header.lotNo || "",
		lotNo: header.lotNo || "",
		remark: header.remark || "",
		valueIndex: header.valueIndex,
		orderIndex: header.orderIndex,
		orderIndexSeq: header.orderIndexSeq,
		checkFinish: "N",
	};
};

/** Detail 목록의 포장량이 모두 동일한지 확인 */
export const areDetailPackQtySame = (detailList: KSK2140_R01DetailDto[]): boolean => {
	if (detailList.length <= 1) return true;
	const firstPackQty = Number(detailList[0].packQty || 0);
	return detailList.every((item) => Number(item.packQty || 0) === firstPackQty);
};

/**
 * 합격량 합계를 포장량 단위로 분배해 Lot 목록 생성.
 * 포장량만큼의 row를 만들고, 나머지는 마지막 row에 배치.
 * (KSK2020_R01 recalculateDetailListWithTransport의 순차 배분 패턴 참고)
 */
export const buildLotListByPackQty = (
	detailList: KSK2140_R01DetailDto[],
): KSK2140_R01LotDto[] => {
	if (detailList.length === 0) return [];

	const packQty = Number(detailList[0].packQty || 0);
	const totalAcceptQty = detailList.reduce(
		(sum, item) => sum + Number(item.acceptQty || 0),
		0,
	);
	if (packQty <= 0 || totalAcceptQty <= 0) return [];

	const firstDetail = detailList[0];
	const fullCount = Math.floor(totalAcceptQty / packQty);
	const remainder = totalAcceptQty % packQty;
	const lotList: KSK2140_R01LotDto[] = [];

	for (let index = 0; index < fullCount; index++) {
		lotList.push({
			...defaultKSK2140_R01LotDto,
			itemCd: firstDetail.itemCd || "",
			itemNm: firstDetail.itemNm || "",
			lotQty: packQty,
			inserted: true,
		});
	}

	if (remainder > 0) {
		lotList.push({
			...defaultKSK2140_R01LotDto,
			itemCd: firstDetail.itemCd || "",
			itemNm: firstDetail.itemNm || "",
			lotQty: remainder,
			inserted: true,
		});
	}

	return lotList;
};

/** 상세가 1건 이상이고 전부 검사완료인지 확인 */
export const isAllDetailCheckFinish = (detailList: KSK2140_R01DetailDto[]): boolean =>
	detailList.length > 0 && detailList.every((item) => item.checkFinish === "Y");

/** 전부 검사완료면 포장량 기준 LOT 목록, 아니면 빈 목록 */
export const getLotListByInspectFinish = (
	detailList: KSK2140_R01DetailDto[],
): KSK2140_R01LotDto[] =>
	isAllDetailCheckFinish(detailList) ? buildLotListByPackQty(detailList) : [];

/** 상세 행의 저장된 검사량. 최초 선택 시 미검사량을 기본값으로 사용 */
export const getSavedInspectQty = (
	detail: KSK2140_R01DetailDto | null | undefined,
	draftByKey: Record<string, number> = {},
) => {
	if (!detail) return 0;
	if (detail.checkFinish === "Y") {
		return Number(detail.inspectQty || 0);
	}
	return draftByKey[getKSK2140_R01InspectKey(detail)] ?? Number(detail.lotQty || 0);
};
